import { Avatar } from "@/components/catalyst/avatar";
import { Badge } from "@/components/catalyst/badge";
import { Subheading } from "@/components/catalyst/heading";
import { Text } from "@/components/catalyst/text";
import { Button } from "@/components/catalyst/button";
import { useGqlQuery, useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { InviteOrganizationMemberDialog } from "@/app/Settings/Settings/Components/InviteOrganizationMemberDialog";
import { useTranslations } from "use-intl";
import { useActiveOrganisation } from "@/app/Organisations/Hooks/useActiveOrganisation";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { parse } from "graphql";

type InvitationRecord = {
  id: string;
  email: string;
  expires_at: string;
  created_at: string;
};

type OrganisationInvitationsData = {
  organisationInvitations: {
    data: InvitationRecord[];
    paginatorInfo: {
      hasMorePages: boolean;
      currentPage: number;
      total: number;
    };
  };
};
type OrganisationInvitationsVars = {
  organisation_id: string;
  first?: number;
  page?: number;
};

export const organisationInvitationsQuery = parse(`
  query OrganisationInvitations(
    $organisation_id: ID!
    $first: Int
    $page: Int
  ) {
    organisationInvitations(
      organisation_id: $organisation_id
      first: $first
      page: $page
    ) {
      data {
        id
        email
        expires_at
        created_at
      }
      paginatorInfo {
        hasMorePages
        currentPage
        total
      }
    }
  }
`) as unknown as TypedDocumentNode<
  OrganisationInvitationsData,
  OrganisationInvitationsVars
>;

type RevokeInvitationData = { revokeInvitation: boolean };
type RevokeInvitationVars = { id: string };

const revokeInvitationMutation = parse(`
  mutation RevokeInvitation($id: ID!) {
    revokeInvitation(id: $id)
  }
`) as unknown as TypedDocumentNode<RevokeInvitationData, RevokeInvitationVars>;

export function OrganizationInvitationsList() {
  const { open: openInviteDialog } = useDialog(InviteOrganizationMemberDialog);
  const { activeOrganisationId } = useActiveOrganisation();
  const t = useTranslations("settings.team.invitations");

  const { data, isLoading, refetch } = useGqlQuery(
    organisationInvitationsQuery,
    {
      organisation_id: activeOrganisationId!,
      first: 20,
      page: 1,
    },
  );

  const { mutateAsync: revokeInvitation, isPending } = useGqlMutation(
    revokeInvitationMutation,
  );

  const invitations = data?.organisationInvitations?.data ?? [];
  const total =
    data?.organisationInvitations?.paginatorInfo?.total ?? invitations.length;

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(date));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Subheading>
          {t("title")} ({total})
        </Subheading>
        <Button outline onClick={() => openInviteDialog({})}>
          {t("inviteMember")}
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center">
          <Text>{t("loadingInvitations")}</Text>
        </div>
      ) : invitations.length === 0 ? (
        <div className="py-8 text-center">
          <Text>{t("noPendingInvitations")}</Text>
        </div>
      ) : (
        <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
          {invitations.map((invitation) => {
            const initials = invitation.email
              ? invitation.email.substring(0, 2).toUpperCase()
              : "??";

            return (
              <div
                key={invitation.id}
                className="flex items-center justify-between py-4"
              >
                <div className="flex items-center gap-4">
                  <Avatar initials={initials} className="size-10" />
                  <div>
                    <p className="text-sm text-zinc-950 dark:text-white">
                      {invitation.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(invitation.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge color="amber">pending</Badge>
                  <Button
                    plain
                    isLoading={isPending}
                    onClick={async () => {
                      await revokeInvitation({
                        id: invitation.id,
                      });
                      refetch();
                    }}
                  >
                    {t("revoke")}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
